/* ============================================================
   Dexcom G7 (10-Day) — Pack selector behaviour
   ------------------------------------------------------------
   On pack change we swap the add-to-cart variant and update the
   displayed price, availability, hero image, and the Appstle
   widget's DISPLAYED prices (one-time + subscribe) for that pack.

   Subscriptions themselves are owned entirely by Appstle: it
   injects and submits the selling_plan on its own (all packs share
   the same plan, which applies to each pack's variant). We never
   touch the selling_plan — doing so previously wiped the
   subscription. We only update what is DISPLAYED.

   All prices come from Shopify (Liquid money + selling_plan
   allocations). No fake ids, prices or plans are created here.
   ============================================================ */
(function () {
  'use strict';

  var SELECTOR_ROOT = '[data-dexcom-pack-selector]';

  function init(root) {
    if (!root || root.dataset.dexcomInit === '1') return;
    root.dataset.dexcomInit = '1';

    var form = root.closest('form[data-type="add-to-cart-form"]') || root.closest('form');
    var infoContainer = root.closest('.t4s-product__info-container') || document;
    var currentProductId = root.getAttribute('data-current-product-id') || '';

    // --- URL preselect (deep links) -----------------------------------
    // Read ?pack=(1|2|3) and ?plan=sub so links open the page with a pack
    // and purchase option already selected. URLSearchParams handles extra
    // params (utm_*, etc.) in any order. pack maps to a card's data-pack-key;
    // plan=sub is applied to Appstle once its widget renders (see below).
    var _params = new URLSearchParams(window.location.search);
    var _packMap = { '1': 'single', '2': 'double', '3': 'triple' };
    var _urlPackKey = _packMap[_params.get('pack')] || null;
    urlPlanSub = (_params.get('plan') || '').toLowerCase() === 'sub';
    if (urlPlanSub) urlPlanDeadline = Date.now() + 4000;

    var urlCard = _urlPackKey
      ? root.querySelector('.dexcom-pack-card[data-pack-key="' + _urlPackKey + '"]')
      : null;
    // Only honour the URL pack if it's actually purchasable; else ignore it.
    if (urlCard && urlCard.getAttribute('data-available') !== 'true') urlCard = null;

    var selectedCard = urlCard ||
                       root.querySelector('.dexcom-pack-card.is--selected') ||
                       root.querySelector('.dexcom-pack-card[data-available="true"]');

    // If a valid pack was requested in the URL, mark it selected now.
    if (urlCard) syncSelectedClass(root, urlCard);

    // Show the default pack's price + savings pill on load (visual only).
    // IMPORTANT: we deliberately do NOT swap the add-to-cart variant here.
    // When the default pack is a DIFFERENT product than the page product
    // (e.g. the 2-Pack), swapping the shared variant id during Appstle's
    // async initialisation prevents Appstle from ever rendering its widget
    // on load — it only reappeared after a manual pack click. So the
    // variant swap is deferred to commitDefaultPack(), fired once Appstle's
    // widget has rendered (see the poll below). This keeps the add-to-cart
    // pointed at the correct pack WITHOUT breaking Appstle's first render.
    if (selectedCard) updatePrice(infoContainer, selectedCard);

    activeCard = selectedCard;
    var initialCommitted = false;

    // Point the ATC form at the default pack's variant + availability.
    // Runs once — after Appstle is ready, or after we stop waiting — so the
    // variant swap never races Appstle's initial render. (Image is left as
    // the page product's to avoid a hero-image flash; it still swaps on an
    // actual pack click.)
    function commitDefaultPack() {
      if (initialCommitted || !activeCard) return;
      initialCommitted = true;
      var v = activeCard.getAttribute('data-variant-id');
      if (form && v) updateVariantId(form, v);
      if (form) updateAvailability(form, activeCard.getAttribute('data-available') === 'true');
      // Re-assert the price AFTER the variant swap (same order as a click),
      // so a URL-preselected 2-Pack/3-Pack always shows its own price/strike/
      // savings on load and can't be left showing the page product's price.
      updatePrice(infoContainer, activeCard);
    }

    // Appstle injects its widget asynchronously. Poll until it appears,
    // then attach an observer that re-asserts the active pack's prices
    // whenever Appstle re-renders (e.g. One-Time/Subscribe toggle).
    var tries = 0;
    var poll = setInterval(function () {
      tries += 1;
      var widget = getAppstleWidget();
      if (widget) {
        if (!appstleObserver) {
          appstleObserver = new MutationObserver(function () { scheduleAppstleApply(); });
          observeAppstle();
        }
        // Appstle has rendered for the page product — now it's safe to
        // point the ATC at the default pack and assert its prices.
        commitDefaultPack();
        applyAppstlePrices(activeCard);
        applyPlanFromUrl();
        clearInterval(poll);
      } else if (tries > 40) {
        // Appstle never rendered (app slow/absent) — still point the ATC
        // at the correct variant so add-to-cart works.
        commitDefaultPack();
        clearInterval(poll);
      }
    }, 500);

    root.addEventListener('change', function (e) {
      var radio = e.target.closest('.dexcom-pack-card__radio');
      if (!radio) return;
      var card = radio.closest('[data-dexcom-pack]');
      if (card) {
        selectedCard = card;
        selectPack(root, card, form, infoContainer, currentProductId);
      }
    });

    if (form) {
      // On One-Time/Subscribe toggle, Appstle re-renders back to the page
      // product's prices — re-assert the active pack's DISPLAYED prices.
      // We do NOT touch the selling_plan; Appstle owns subscription entirely.
      form.addEventListener('change', function (e) {
        if (e.target && e.target.name === 'selling_plan') {
          scheduleAppstleApply();
        }
      });
    }
  }

  function selectPack(root, card, form, infoContainer, currentProductId) {
    if (card.getAttribute('data-available') === 'false') return;

    syncSelectedClass(root, card);

    var variantId = card.getAttribute('data-variant-id');
    var price = card.getAttribute('data-price');
    var compare = card.getAttribute('data-compare');
    var image = card.getAttribute('data-image');
    var available = card.getAttribute('data-available') === 'true';

    activeCard = card;
    if (form && variantId) updateVariantId(form, variantId);
    updatePrice(infoContainer, card);
    if (form) updateAvailability(form, available);
    if (image) updateImage(image);
    applyAppstlePrices(card);
    scheduleAppstleApply();

    document.dispatchEvent(new CustomEvent('dexcom:pack:changed', {
      detail: {
        packKey: card.getAttribute('data-pack-key'),
        productId: card.getAttribute('data-product-id'),
        variantId: variantId,
        url: card.getAttribute('data-url'),
        hasSubscription: card.getAttribute('data-has-subscription') === 'true',
        sellingPlanId: card.getAttribute('data-selling-plan-id') || ''
      }
    }));
  }

  function syncSelectedClass(root, card) {
    root.querySelectorAll('.dexcom-pack-card').forEach(function (c) {
      c.classList.toggle('is--selected', c === card);
    });
  }

  function updateVariantId(form, variantId) {
    var idInput = form.querySelector('input[name="id"], select[name="id"]');
    if (!idInput) {
      idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = 'id';
      form.appendChild(idInput);
    }
    idInput.value = variantId;
    idInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function updatePrice(container, card) {
    if (!card) return;
    var priceHtml = card.getAttribute('data-price');
    if (!priceHtml) return;
    var compareHtml = card.getAttribute('data-compare');
    var saveAmount = card.getAttribute('data-save-amount');
    var savePct = card.getAttribute('data-save-pct');
    var priceNode = container.querySelector('.t4s-product__price-review [data-product-price]') ||
                    container.querySelector('[data-product-price]');
    if (!priceNode) return;

    var html;
    if (compareHtml) {
      html = '<del>' + compareHtml + '</del> <ins>' + priceHtml + '</ins>';
    } else {
      html = priceHtml;
    }
    if (compareHtml && saveAmount) {
      html += ' <span class="dexcom-save-pill">Save ' + saveAmount +
              (savePct ? ' (' + savePct + '%)' : '') + '</span>';
    }
    priceNode.innerHTML = html;
  }

  function updateAvailability(form, available) {
    var btn = form.querySelector('[data-atc-form]');
    if (!btn) return;
    var label = btn.querySelector('.t4s-btn-atc_text');
    if (available) {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
    } else {
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-disabled', 'true');
      if (label) label.textContent = 'Sold out';
    }
  }

  function updateImage(imageUrl) {
    try {
      var mediaWrap = document.querySelector('.t4s-product__media-wrapper');
      if (!mediaWrap) return;
      var img = mediaWrap.querySelector('[data-main-media] img, .t4s-product__media img, img');
      if (!img) return;
      img.setAttribute('src', imageUrl);
      if (img.hasAttribute('data-src')) img.setAttribute('data-src', imageUrl);
      if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
    } catch (err) { /* image swap is non-critical */ }
  }

  /* --- Phase 4b: update the Appstle widget's displayed prices in place ---
     Appstle renders its widget for the PAGE product and RE-renders it back
     to the page product's prices on every One-Time/Subscribe toggle. We
     re-assert the ACTIVE pack's real prices after each render. To avoid a
     race we (a) pause the observer while writing and (b) re-apply across a
     few frames so we reliably win after Appstle finishes rendering. */
  var appstleObserver = null;
  var activeCard = null;
  // URL preselect (?plan=sub): pick Appstle's "Subscribe & Save" on load.
  var urlPlanSub = false;
  var urlPlanDeadline = 0;

  function getAppstleWidget() {
    return document.querySelector('[id^="appstle_subscription_widget"]') ||
           document.querySelector('.appstle_sub_widget');
  }

  function observeAppstle() {
    var widget = getAppstleWidget();
    if (!widget || !appstleObserver) return;
    appstleObserver.observe(widget.parentElement || widget, {
      childList: true, subtree: true, characterData: true
    });
  }

  // Re-assert the active pack's prices across a few frames so we win the
  // race against Appstle's async re-render (fixes wrong price + flicker).
  function scheduleAppstleApply() {
    if (!activeCard) return;
    requestAnimationFrame(function () { applyAppstlePrices(activeCard); applyPlanFromUrl(); });
    setTimeout(function () { applyAppstlePrices(activeCard); applyPlanFromUrl(); }, 60);
    setTimeout(function () { applyAppstlePrices(activeCard); applyPlanFromUrl(); }, 200);
  }

  function setAmount(scope, selector, moneyHtml, srPrefix, srSelector) {
    if (!scope || !moneyHtml) return;
    var el = scope.querySelector(selector);
    if (el) el.innerHTML = moneyHtml;
    if (srSelector) {
      var sr = scope.querySelector(srSelector);
      if (sr) sr.innerHTML = (srPrefix || '') + moneyHtml;
    }
  }

  // Appstle WIDGET_TYPE_7 markup:
  //  One-Time:  .appstle_one_time_price_wrapper .appstle_subscription_amount
  //  Subscribe: .appstle_subscription_amount_wrapper .appstle_subscription_amount
  //             + .appstle_subscription_compare_amount (strikethrough)
  function applyAppstlePrices(card) {
    if (!card) return;
    var widget = getAppstleWidget();
    if (!widget) return;
    var oneTime = card.getAttribute('data-price');
    var subPrice = card.getAttribute('data-subscribe-price');
    var subCompare = card.getAttribute('data-subscribe-compare');

    // Pause observing so our own writes don't retrigger the observer.
    if (appstleObserver) appstleObserver.disconnect();

    var otWrap = widget.querySelector('.appstle_one_time_price_wrapper');
    setAmount(otWrap, '.appstle_subscription_amount', oneTime,
              'Sale price ', '.appstle_subscription_amount-appstle-sr-only');

    var subWrap = widget.querySelector('.appstle_subscription_amount_wrapper');
    setAmount(subWrap, '.appstle_subscription_amount', subPrice,
              'Sale price ', '.appstle_subscription_amount-appstle-sr-only');
    setAmount(subWrap, '.appstle_subscription_compare_amount', subCompare,
              'Original price ', '.appstle_subscription_compare_amount-appstle-sr-only');

    ensureMostPopularBadge(widget);

    if (appstleObserver) observeAppstle();
  }

  // Item 2: inject a "Most popular" badge into the Subscribe & Save
  // option, placed just before Appstle's "SAVE 10%" badge. Idempotent
  // (skips if already present) and re-run on every apply cycle so it
  // survives Appstle's re-renders. Runs only on this page because this
  // script loads solely on the dexcom-g7-10-day template. The DOM insert
  // happens while the observer is disconnected (see applyAppstlePrices),
  // so it doesn't retrigger the observer.
  function ensureMostPopularBadge(widget) {
    var wrap = widget.querySelector('.appstle-text-and-badge-wrapper');
    if (!wrap || wrap.querySelector('.dexcom-appstle-popular')) return;
    var badge = document.createElement('span');
    badge.className = 'dexcom-appstle-popular';
    badge.textContent = 'Most popular';
    var saveBadge = wrap.querySelector('.appstle-save-badge');
    if (saveBadge) {
      wrap.insertBefore(badge, saveBadge);
    } else {
      wrap.appendChild(badge);
    }
  }

  // URL preselect for the purchase option: if ?plan=sub is in the URL, select
  // Appstle's "Subscribe & Save" radio once its widget has rendered. We click
  // the real radio (mimicking a user) so Appstle updates fully, and re-try on
  // each apply cycle until it sticks or a short deadline passes — this wins
  // Appstle's async re-renders without fighting a later manual switch back to
  // One-Time. We never set the selling_plan ourselves; Appstle owns that.
  function applyPlanFromUrl() {
    if (!urlPlanSub) return;
    var widget = getAppstleWidget();
    if (!widget) return;
    var subRadio = widget.querySelector('.appstle_subscription_radio_wrapper input[name="selling_plan"]');
    if (!subRadio) return;
    if (subRadio.checked) { urlPlanSub = false; return; } // selected — stop re-trying
    if (Date.now() < urlPlanDeadline) subRadio.click();
  }

  function boot() {
    document.querySelectorAll(SELECTOR_ROOT).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
